import { useEffect, useRef, useState } from 'react'
import {
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Divider,
  Flex,
  Spin,
  message,
} from 'antd'
import { useNavigate, useParams, Outlet } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'
import type { Bid, Partner } from '@/data'
import { getPartner } from '@/api/lop/partners/getPartner'
import { getBidRungs } from '@/api/lop/bid-rungs/getBidRungs'
import {
  createBidRung,
  type BidRungPayload,
} from '@/api/lop/bid-rungs/createBidRung'
import {
  syncBidRungs,
  type BidRungSyncItem,
} from '@/api/lop/bid-rungs/syncBidRungs'

const rungConditions = (bid: Bid) =>
  bid.conditions.map(({ field, operator, value }) => ({
    field,
    operator,
    value,
  }))

const buildPayload = (
  bid: Bid,
  partner_id: number,
  priority: number,
): BidRungPayload => ({
  partner_id,
  name: bid.name,
  bid_amount: bid.bid_amount,
  priority,
  active: bid.active,
  conditions: rungConditions(bid),
})

const buildSyncItem = (bid: Bid, priority: number): BidRungSyncItem => ({
  id: bid.id,
  name: bid.name,
  bid_amount: bid.bid_amount,
  priority,
  active: bid.active,
  conditions: rungConditions(bid),
})

const { Title } = Typography
const { Content } = Layout

export interface BiddingEditorContext {
  partnerId: number
  partner: Partner | null
  bids: Bid[]
  setBids: (b: Bid[]) => void
}

const BiddingEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const { id: idParam } = useParams<{ id: string }>()
  const numericId = idParam != null && idParam !== '' ? Number(idParam) : null
  const idIsValid = numericId != null && Number.isFinite(numericId)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notFound, setNotFound] = useState(false)

  const [partner, setPartner] = useState<Partner | null>(null)
  const [bids, setBids] = useState<Bid[]>([])

  const hasFetched = useRef(false)

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    if (!idIsValid || numericId == null) {
      setNotFound(true)
      setLoading(false)
      return
    }
    const partnerId = numericId
    Promise.all([
      getPartner(String(partnerId)),
      getBidRungs({
        filters: [{ key: 'partner_id', value: String(partnerId) }],
        include: 'conditions',
        per_page: 100,
      }),
    ])
      .then(([partnerData, rungs]) => {
        if (!partnerData) {
          setNotFound(true)
          return
        }
        setPartner(partnerData)
        const sorted = [...rungs.data].sort((a, b) => a.priority - b.priority)
        setBids(sorted)
      })
      .catch((err) => {
        console.error(err)
        message.error('Failed to load bid ladder')
      })
      .finally(() => setLoading(false))
  }, [idIsValid, numericId])

  const handleSave = async () => {
    if (numericId == null) return
    if (bids.length === 0) {
      message.info('Nothing to save')
      return
    }
    const drafts = bids.filter((b) => b.id <= 0)
    if (drafts.some((d) => !d.name.trim())) {
      message.error('All bids must have a name')
      return
    }
    try {
      setSaving(true)
      const persistedMax = Math.max(
        0,
        ...bids.filter((b) => b.id > 0).map((b) => b.priority),
      )
      const draftMap = new Map<number, Bid>()
      for (let i = 0; i < drafts.length; i++) {
        const d = drafts[i]
        const rung = await createBidRung(
          buildPayload(d, numericId, persistedMax + i + 1),
        )
        draftMap.set(d.id, rung)
      }
      const withIds: Bid[] = bids.map((b) =>
        b.id <= 0 ? (draftMap.get(b.id) ?? b) : b,
      )
      const synced = await syncBidRungs({
        partner_id: numericId,
        rungs: withIds.map((b, i) => buildSyncItem(b, i + 1)),
      })
      setBids([...synced].sort((a, b) => a.priority - b.priority))
      message.success('Bid ladder saved')
    } catch (err) {
      message.error('Failed to save bid ladder')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  if (notFound) {
    return (
      <Flex vertical gap="middle" align="center" style={{ paddingTop: 80 }}>
        <Title level={4}>Partner not found</Title>
        <Button type="primary" onClick={() => navigate('/bidding')}>
          Back to Bidding
        </Button>
      </Flex>
    )
  }

  const context: BiddingEditorContext = {
    partnerId: numericId!,
    partner,
    bids,
    setBids,
  }

  const partnerName = partner?.name ?? `Partner ${numericId}`

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: 'Bidding', href: '/bidding' },
            { title: `Edit: ${partnerName}` },
          ]}
        />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/bidding')}
          />
          <Title level={3} style={{ margin: 0 }}>
            Bid Ladder: {partnerName}
          </Title>
        </Flex>

        <Content style={{ flex: 1, minHeight: 400 }}>
          <Outlet context={context} />

          <Divider />

          <Flex justify="flex-end" style={{ paddingTop: 16 }}>
            <Button type="primary" onClick={handleSave} loading={saving}>
              Save Changes
            </Button>
          </Flex>
        </Content>
      </Flex>
    </div>
  )
}

export const BiddingEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  return <BiddingEditorContent key={id ?? 'new'} />
}
